import { ReactNode } from "react"
import { createInertiaApp, usePage } from "@inertiajs/react"
import { createRoot } from "react-dom/client"

import { Layout } from "@/components/ui/layout"
import { WorkspaceDefinitionsContext } from "@/components/workspace/definition"
import { pageLayout, pageModule } from "@/composition/pages"
import { workspaces } from "@/composition/workspaces"
import { registerPages } from "@/lib/page-rendering/page-registry"
import { ApiProvider } from "@/api"

import "@/i18n"
import "@fortawesome/fontawesome-free/css/all.min.css" // Import FontAwesome
import "temporal-polyfill/global"

// The boot: the composition root's page map is given to the shell, and the
// app is drawn over Inertia with the composed workspace definitions above
// every page.
registerPages(pageModule)

// The layout around every page, which Inertia keeps mounted across visits.
// It reads the page's layout from its registry entry: an editor page fills
// the viewport, any other scrolls with the document. A page in an iframe is
// a sheet's, drawn without the header. Nothing is read from the page's
// module: every fact the shell needs is in the registries.
function InertiaPageLayout({ children }: { children: ReactNode }) {
  const { component } = usePage()

  return (
    <Layout
      fullScreen={pageLayout(component) === "editor"}
      iframe={window.parent !== window}
    >
      {children}
    </Layout>
  )
}

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.netlify.app/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.netlify.app/guide/progress-indicators
  // progress: false,

  resolve: (name) => pageModule(name),

  layout: () => InertiaPageLayout,

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        <ApiProvider>
          <WorkspaceDefinitionsContext value={workspaces}>
            <App {...props} />
          </WorkspaceDefinitionsContext>
        </ApiProvider>,
      )
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})
